import React, { useEffect, useMemo, useState } from "react";
import Button from "./Button";
import {
  buildSpotifyAuthorizeUrl,
  clearSpotifyAuthScratchKeys,
  emitSpotifyAuthChanged,
  SPOTIFY_AUTH_CHANGED_EVENT,
} from "../services/spotifyAuth";
import { SpotifyService } from "../services/spotifyService";

type Props = {
  clientId: string;
};

export default function SpotifyAuth({ clientId }: Props) {
  const redirectUri = useMemo(
    () =>
      process.env.REACT_APP_SPOTIFY_REDIRECT_URI ||
      `${window.location.origin}/oauth/spotify-callback`,
    [],
  );

  const spotifyService = useMemo(() => new SpotifyService(), []);

  const [error, setError] = useState<string | null>(null);
  const [debug, setDebug] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const refreshState = async () => {
      try {
        const token = await spotifyService.getSpotifyToken();
        setIsConnected(Boolean(token?.accessToken));
      } catch {
        setIsConnected(false);
      }
    };

    refreshState();

    const handleAuthChanged = () => {
      refreshState();
    };

    window.addEventListener(SPOTIFY_AUTH_CHANGED_EVENT, handleAuthChanged);

    return () => {
      window.removeEventListener(SPOTIFY_AUTH_CHANGED_EVENT, handleAuthChanged);
    };
  }, [spotifyService]);

  const connect = async () => {
    setError(null);
    setDebug(null);

    if (!clientId || clientId.trim() === "") {
      setError(
        "Client ID is not configured. Check REACT_APP_SPOTIFY_CLIENT_ID in .env",
      );
      return;
    }

    try {
      const authUrl = await buildSpotifyAuthorizeUrl(clientId, redirectUri);

      console.log("=== Spotify OAuth Debug Info ===");
      console.log("Redirect URI:", redirectUri);
      console.log("Client ID:", clientId);
      console.log("Full OAuth URL:", authUrl);
      console.log("===============================");

      setDebug("Redirecting to Spotify... Check console for details");
      setTimeout(() => {
        window.location.href = authUrl;
      }, 400);
    } catch (e: any) {
      setError(e?.message ?? "Failed to initiate Spotify OAuth flow");
    }
  };

  const disconnect = async () => {
    try {
      await spotifyService.deleteSpotifyToken();
      clearSpotifyAuthScratchKeys();
      emitSpotifyAuthChanged();
      setDebug("Spotify disconnected");
      setIsConnected(false);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? "Failed to disconnect Spotify");
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button
          onClick={isConnected ? disconnect : connect}
          className="primary-btn p-3"
        >
          {isConnected ? "Disconnect Spotify" : "Connect Spotify"}
        </Button>
      </div>

      <div style={{ marginBottom: 8 }}>
        Status: <strong>{isConnected ? "Connected" : "Not connected"}</strong>
      </div>
      <div className="text-small" style={{ opacity: 0.85 }}>
        Redirect URI: {redirectUri}
      </div>

      {debug && <div style={{ marginTop: 8, color: "blue" }}>{debug}</div>}
      {error && <div style={{ marginTop: 8, color: "crimson" }}>{error}</div>}
    </div>
  );
}
